import { useState, useEffect, useMemo } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { TopNav, Sidebar, MobileNav } from 'components/navigation/TopNav';
import { LearnerLocationBar } from 'components/navigation/LearnerLocationBar';
import { MobileBottomNav } from 'components/navigation/MobileBottomNav';
import { MobileSearchSheet } from 'components/navigation/MobileSearchSheet';
import { PageTransition } from 'components/ui/PageTransition';
import { SiteFooter } from 'components/layout/SiteFooter';
import { LearnerEthicsModal } from 'components/navigation/LearnerEthicsModal';
import { AnnouncementBanner } from 'features/announcements/components/AnnouncementBanner';
import { EnrollmentNoticeBanner } from 'features/enrollment/components/EnrollmentNoticeBanner';
import { BhriguFloatingChat } from 'features/ai/components/BhriguFloatingChat';
import { ParashariAppProviders } from 'schools/parashari/providers/ParashariAppProviders';
import { ParashariLearnNavProvider } from 'schools/parashari/contexts/ParashariLearnNavContext';
import { useAuth } from 'schools/parashari/contexts/AuthContext';
import { useLearningConsent } from 'schools/parashari/hooks/useLearningConsent';
import { useAppSelector } from 'hooks/useAppStore';
import { ROUTES } from 'constants/routes';
import { learnerNav } from 'constants/copy';
import { cn } from 'utils/cn';
import { isSchoolLearnPath, isSchoolTypeScalePath } from 'utils/learnerNavActive';
import { buildLearnerNavLinks } from 'constants/learnerNavConfig';

function AppLayoutInner() {
  const [ethicsOpen, setEthicsOpen] = useState(false);
  const location = useLocation();
  const { user } = useAuth();
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated);
  const authLoading = useAppSelector((s) => s.auth.isLoading);
  // Institute destinations are empty without an active batch enrollment - hide them.
  const hasInstitute = useAppSelector((s) => Boolean(s.auth.user?.hasApprovedMembership));
  const appLinks = useMemo(() => buildLearnerNavLinks(hasInstitute), [hasInstitute]);
  const { hasConsent, consentChecked, consentBusy, consentError, acceptConsent } =
    useLearningConsent();
  const isProgramsRoute =
    location.pathname === ROUTES.PROGRAMS ||
    location.pathname.startsWith(`${ROUTES.PROGRAMS}/`);
  const isGuestProgramsRoute = isProgramsRoute && !authLoading && !isAuthenticated;
  const showLearnerNav = !isGuestProgramsRoute;
  // Full-bleed routes manage their own padding and span the full viewport width.
  const isFullBleedRoute =
    isSchoolLearnPath(location.pathname) ||
    location.pathname === ROUTES.SCHOOLS ||
    location.pathname === ROUTES.AI_TUTOR ||
    location.pathname === ROUTES.AI_CHART_LAB ||
    isProgramsRoute;
  // School-module pages keep the larger reading scale from parashari-styles.css;
  // platform shell pages use the fluid ramp from the shared Tailwind preset.
  const usesSchoolTypeScale = isSchoolTypeScalePath(location.pathname);
  // Gate on consentChecked only - it stays false until the DB fetch finishes.
  const showRequiredEthics = Boolean(user && consentChecked && !hasConsent);

  useEffect(() => {
    if (!showLearnerNav) return undefined;
    document.documentElement.classList.add('site-has-mobile-tab');
    return () => {
      document.documentElement.classList.remove('site-has-mobile-tab');
    };
  }, [showLearnerNav]);

  return (
    <ParashariLearnNavProvider>
      <div className="min-h-screen flex flex-col">
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[var(--z-toast)] focus:rounded-lg focus:bg-surface focus:px-4 focus:py-2.5 focus:text-sm focus:font-semibold focus:text-content focus:shadow-modal focus:outline-none focus:ring-2 focus:ring-accent"
        >
          Skip to main content
        </a>
        <TopNav onOpenEthics={() => setEthicsOpen(true)} />
        {showLearnerNav ? <EnrollmentNoticeBanner /> : null}
        {showLearnerNav ? <AnnouncementBanner /> : null}
        {showLearnerNav ? <LearnerLocationBar /> : null}
        <div className="flex flex-1">
          {showLearnerNav ? <Sidebar links={appLinks} title={learnerNav.sidebarTitle} /> : null}
          {showLearnerNav ? <MobileNav links={appLinks} /> : null}
          <main
            id="main-content"
            tabIndex={-1}
            className={cn(
              'site-main-with-fixed-footer flex-1 min-w-0 overflow-auto',
              showLearnerNav ? 'pb-bottom-nav md:pb-4' : 'pb-4',
              isFullBleedRoute ? 'p-0' : 'p-4 lg:p-8'
            )}
          >
            <div
              className={cn(
                'mx-auto min-w-0',
                isFullBleedRoute ? 'max-w-none' : 'max-w-7xl',
                usesSchoolTypeScale && 'school-type-scale'
              )}
            >
              <PageTransition>
                <Outlet />
              </PageTransition>
            </div>
          </main>
        </div>
        <SiteFooter className={showLearnerNav ? 'pb-bottom-nav md:pb-0' : undefined} />
        {showLearnerNav ? <MobileBottomNav /> : null}
        {showLearnerNav ? <MobileSearchSheet /> : null}
        {showLearnerNav ? <BhriguFloatingChat pathname={location.pathname} /> : null}
        <LearnerEthicsModal
          open={ethicsOpen || showRequiredEthics}
          required={showRequiredEthics}
          hasConsent={hasConsent}
          consentBusy={consentBusy}
          consentError={consentError}
          onAcceptConsent={acceptConsent}
          onClose={() => {
            if (!showRequiredEthics) setEthicsOpen(false);
          }}
        />
      </div>
    </ParashariLearnNavProvider>
  );
}

export function AppLayout() {
  return (
    <ParashariAppProviders>
      <AppLayoutInner />
    </ParashariAppProviders>
  );
}
